import React, { useCallback, useState } from "react";
import { useConfig } from "../../../contexts/ConfigProvider";
import validateUser from "../utils/validateUser";
import { VerificationError } from "../types";
import Auth0 from "../../../auth/service";
import { FieldNames } from "../types";
import type { InputValue } from "@bmi-digital/components/form";
import type { EmailStatus } from "../types";

export type Props = {
  setEmailStatus: (emailStatus: EmailStatus) => void;
  redirectURL: string | null;
};

type UseSubmitForm = (params: Props) => {
  handleSubmit: (
    event: React.FormEvent,
    values: Record<string, InputValue>
  ) => Promise<void>;
  isSubmitting: boolean;
};

const useSubmitForm: UseSubmitForm = ({ setEmailStatus, redirectURL }) => {
  const { intouchOrigin, intouchMarketCode } = useConfig();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const handleSubmit = useCallback(
    async (event: React.FormEvent, values: Record<string, InputValue>) => {
      event.preventDefault();
      setIsSubmitting(true);

      if (!intouchOrigin || !intouchMarketCode) {
        console.error(
          "intouchOrigin or intouchMarketCode is not defined in the Netlify config"
        );
        setIsSubmitting(false);
        return;
      }

      const email = values.email as string;

      try {
        const response = await validateUser(
          intouchOrigin,
          email,
          intouchMarketCode
        );

        if (!response) {
          console.error("Did not manage to verify user");
          setIsSubmitting(false);
          return;
        }

        if (response.errors?.length) {
          const alreadyExists = !!response.errors.find(
            (error) => error.code === VerificationError.USER_ALREADY_EXISTS
          );
          const needsVerification = !!response.errors.find(
            (error) => error.code === VerificationError.USER_NOT_VERIFIED
          );

          setEmailStatus({
            alreadyExists,
            needsVerification,
            lastBrokenEmail:
              alreadyExists || needsVerification ? email : undefined
          });

          setIsSubmitting(false);
          return;
        }

        await Auth0.embeddedSignUp(
          {
            email: email,
            market: intouchMarketCode,
            firstName: values[FieldNames.firstName] as string,
            lastName: values[FieldNames.lastName] as string,
            password: values[FieldNames.password] as string
          },
          redirectURL
        );

        setIsSubmitting(false);
      } catch (err) {
        setIsSubmitting(false);
        console.error(err);
      }
    },
    [intouchOrigin, intouchMarketCode, redirectURL]
  );

  return { handleSubmit, isSubmitting };
};

export default useSubmitForm;
